import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IComexBankFee, defaultValue } from 'app/shared/model/comex-bank-fee.model';

const initialState: EntityState<IComexBankFee> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/comex-bank-fees';

// Actions

export const getEntities = createAsyncThunk('comexBankFee/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IComexBankFee[]>(requestUrl);
});

export const getFeeListByBankId = createAsyncThunk(
  'comexBankFee/fetch_fee_list_by_bank_id',
  async (bankId: number | string) => {
    const requestUrl = `${apiUrl}/getFeeListByBankId/${bankId}`;
    return axios.get<IComexBankFee[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getFeeListByBankIdAndCommodityType = createAsyncThunk(
  'comexBankFee/fetch_fee_list_by_bank_id_and_commodity_type',
  async ({ bankId, commodityType }: { bankId: number | string; commodityType: string }) => {
    const requestUrl = `${apiUrl}/getFeeListByBankIdAndCommodityType/${bankId}/${commodityType}`;
    return axios.get<IComexBankFee[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'comexBankFee/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IComexBankFee>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'comexBankFee/create_entity',
  async (entity: IComexBankFee, thunkAPI) => {
    const result = await axios.post<IComexBankFee>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'comexBankFee/update_entity',
  async (entity: IComexBankFee, thunkAPI) => {
    const result = await axios.put<IComexBankFee>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'comexBankFee/partial_update_entity',
  async (entity: IComexBankFee, thunkAPI) => {
    const result = await axios.patch<IComexBankFee>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'comexBankFee/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IComexBankFee>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntityFromBank = createAsyncThunk(
  'comexBankFee/delete_entity',
  async ({ id, bankId, fromTo }: { id: string | number; bankId: string | number; fromTo: string | number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IComexBankFee>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const ComexBankFeeSlice = createEntitySlice({
  name: 'comexBankFee',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getFeeListByBankId, getFeeListByBankIdAndCommodityType), (state, action) => {
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ComexBankFeeSlice.actions;

// Reducer
export default ComexBankFeeSlice.reducer;
